import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import ArrowLeftIcon from "../../resources/icons/arrow-narrow-left.svg";
import { useNavigate } from "react-router-dom";
import CategoryCard from "../../components/Cards/CategoryCard/CategoryCard";
import { GET } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import CalculateCardsPerRow from "../../service/CalculateCardsPerRow";
import CfLoader from "../../common/CfLoader/CfLoader";

const Categories = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const [limit, setLimit] = useState(0);
  const [offset, setOffset] = useState(0);
  const [categoriesList, setCategoriesList] = useState([]);
  const [fullCategoriesList, setFullCategoriesList] = useState([]);
  const [count, setCount] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [noResults, setNoResults] = useState(false);

  const cardWidth = "19.125rem";
  const gap = "0.75rem";

  const GET_CATEGORIES_LIST = useCallback(
    async (currentOffset) => {
      if (
        isLoading ||
        isLoadingMore ||
        (currentOffset > 0 && categoriesList.length >= count)
      ) {
        return;
      }

      if (currentOffset === 0) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const res = await GET(
          `${EntityConstants.DISCOVERY_DEFAULT_CATEGORY}?limit=${limit}&offset=${currentOffset}`
        );
        const data = await res.json();

        setCount(data.count);
        if (currentOffset === 0) {
          setFullCategoriesList(data.results);
        }
        setCategoriesList((prev) =>
          currentOffset === 0 ? data.results : [...prev, ...data.results]
        );
        setOffset(currentOffset + data.results.length);
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [isLoading, isLoadingMore, categoriesList.length, count, limit]
  );

  const SEARCH = async (e) => {
    const term = e.target.value.trim();
    if (term.length > 2) {
      setIsSearching(true);
      setIsSearched(true);
      setNoResults(false);

      try {
        const res = await GET(`${EntityConstants.DISCOVERY_SEARCH}${term}`);
        const data = await res.json();
        const cats = data.categories || [];
        setCategoriesList(cats);
        setCount(cats.length);
        if (cats.length === 0) setNoResults(true);
      } catch (err) {
        console.error("Search error:", err);
        setCategoriesList([]);
        setNoResults(true);
      } finally {
        setIsSearching(false);
      }
    } else {
      setIsSearched(false);
      setNoResults(false);
      setCategoriesList(fullCategoriesList);
      setOffset(0);
      GET_CATEGORIES_LIST(0);
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      const cpr = CalculateCardsPerRow(containerRef, cardWidth, gap);
      const newLimit = cpr < 4 ? 8 : cpr * 2;
      setLimit(newLimit);
    }
  }, [cardWidth, gap]);

  useEffect(() => {
    if (limit > 0 && !isSearched) {
      setCategoriesList([]);
      setOffset(0);
      GET_CATEGORIES_LIST(0);
    }
  }, [limit, isSearched]);

  useEffect(() => {
    const onScroll = () => {
      const atBottom =
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 200;

      if (
        atBottom &&
        !isLoading &&
        !isLoadingMore &&
        !isSearching &&
        !isSearched &&
        categoriesList.length < count
      ) {
        GET_CATEGORIES_LIST(offset);
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [
    isLoading,
    isLoadingMore,
    isSearching,
    isSearched,
    categoriesList.length,
    count,
    offset,
    GET_CATEGORIES_LIST,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="h-full">
      <div className="bxm cbd font-CfPrimaryFont bg-cfSecondary">
        <div className="flex flex-col items-center w-full min-h-screen">
          <div
            className={`w-full bg-cfSearchContainer flex justify-center py-[0.8rem] px-4`}
          >
            <div
              className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full`}
            >
              <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-center md:self-start my-auto">
                <button
                  onClick={() => navigate(-1)}
                  className="cursor-pointer focus:outline-none"
                >
                  <img
                    src={ArrowLeftIcon}
                    alt="Back"
                  />
                </button>
                <p>Categories</p>
              </div>
              {/* Search */}
              <form className=" bg-cfSecondary p-2 rounded-lg w-full sm:w-[25rem] self-center ml-[0.630rem] md:self-end md:m-0">
                <input
                  type="text"
                  name="search"
                  id="search"
                  onChange={SEARCH}
                  placeholder="Search in categories"
                  className="outline-none border-none p-0 placeholder:text-sm w-full"
                  style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
                />
              </form>
            </div>
          </div>
          <div ref={containerRef} className="flex flex-wrap gap-6 w-full p-4">
            {isSearching ? (
              <div className="w-full flex justify-center p-4">
                <CfLoader />
              </div>
            ) : noResults ? (
              <p className="text-grey-50 text-center w-full font-semibold text-lg mt-4">
                No results found
              </p>
            ) : (
              categoriesList.map(
                (
                  {
                    category_id,
                    category_name,
                    category_description,
                    tags,
                    thumbnail_images,
                    credentials_count,
                  },
                  index
                ) => (
                  <>
                    {credentials_count > 0 && (
                      <CategoryCard
                        key={index}
                        category_name={category_name}
                        category_description={category_description}
                        category_id={category_id}
                        tagsList={tags}
                        credentials_count={credentials_count}
                        thumbnail_image_1={
                          thumbnail_images?.length > 0 && thumbnail_images[0]
                        }
                        thumbnail_image_2={
                          thumbnail_images?.length > 0 && thumbnail_images[1]
                        }
                        thumbnail_image_3={
                          thumbnail_images?.length > 0 && thumbnail_images[2]
                        }
                      />
                    )}
                  </>
                )
              )
            )}
          </div>

          {(isLoading || isLoadingMore) && !isSearching && (
            <div className="flex justify-center p-4">
              <CfLoader />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Categories;
